import React, { useMemo, useState } from "react";
import { fmtMoney, fmtNumber } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Percent, TrendingUp, AlertTriangle, Info, Calculator } from "lucide-react";
import HireUsCTA from "@/components/HireUsCTA";
import EmailResultsCard from "@/components/EmailResultsCard";
import NudgeCard from "@/components/NudgeCard";

/**
 * Convert between:
 *  - Cost + Margin %  -> Sell, Profit, Markup %
 *  - Cost + Markup %  -> Sell, Profit, Margin %
 *  - Cost + Sell      -> Profit, Margin %, Markup %
 */
function compute(mode, cost, value, sell) {
  const c = Number(cost) || 0;
  if (mode === "margin") {
    const mPct = Math.min(Math.max(Number(value) || 0, -99.99), 99.99);
    const m = mPct / 100;
    const s = m >= 1 ? 0 : c / (1 - m);
    const profit = s - c;
    const markup = c > 0 ? (profit / c) * 100 : 0;
    return { cost: c, sell: s, profit, margin: mPct, markup };
  }
  if (mode === "markup") {
    const k = (Number(value) || 0) / 100;
    const s = c * (1 + k);
    const profit = s - c;
    const margin = s > 0 ? (profit / s) * 100 : 0;
    return { cost: c, sell: s, profit, margin, markup: Number(value) || 0 };
  }
  // "price" mode
  const s = Number(sell) || 0;
  const profit = s - c;
  const margin = s > 0 ? (profit / s) * 100 : 0;
  const markup = c > 0 ? (profit / c) * 100 : 0;
  return { cost: c, sell: s, profit, margin, markup };
}

const REFERENCE = [
  { markup: 10, margin: 9.09 },
  { markup: 15, margin: 13.04 },
  { markup: 20, margin: 16.67 },
  { markup: 25, margin: 20.0 },
  { markup: 33.33, margin: 25.0 },
  { markup: 50, margin: 33.33 },
  { markup: 66.67, margin: 40.0 },
  { markup: 100, margin: 50.0 },
  { markup: 150, margin: 60.0 },
  { markup: 200, margin: 66.67 },
];

export default function MarginMarkupCalc() {
  const { t } = useLang();
  const [mode, setMode] = useState("markup"); // margin | markup | price
  const [cost, setCost] = useState(1000);
  const [value, setValue] = useState(30); // % value for margin/markup modes
  const [sell, setSell] = useState(1300);

  const r = useMemo(() => compute(mode, cost, value, sell), [mode, cost, value, sell]);

  // "If you MEANT margin but USED markup" — classic mistake
  const meantMargin = useMemo(() => {
    const pct = Number(value) || 0;
    const targetMargin = pct / 100;
    if (targetMargin >= 1) return null;
    const correctSell = Number(cost) / (1 - targetMargin);
    const mistakenSell = Number(cost) * (1 + targetMargin);
    const lost = correctSell - mistakenSell;
    return { correctSell, mistakenSell, lost, pct };
  }, [cost, value]);

  return (
    <div className="stagger-in">
      <div className="label-mono">/ {t("common.calculator", "CALCULATOR")} · 05</div>
      <h1 className="font-display text-5xl tracking-tight mt-2">{t("margin.h1", "Margin vs Markup")}</h1>
      <p className="text-zinc-400 mt-2 max-w-3xl">
        {t("margin.sub", "Same profit in dollars — very different percentages. Getting these two mixed up is the #1 reason tradies quote jobs that look profitable on paper but leave barely anything in the bank.")}
      </p>

      {/* EXPLAINER */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 mt-10 border border-[var(--border)]">
        <div className="p-4 sm:p-6 md:p-8 border-r border-[var(--border)]">
          <div className="flex items-center gap-2">
            <Percent size={16} className="text-[var(--primary)]" />
            <div className="label-mono">MARKUP</div>
          </div>
          <div className="font-mono text-lg mt-3 text-[var(--primary)]">
            profit ÷ <span className="text-white">cost</span>
          </div>
          <p className="text-zinc-300 mt-4 text-sm leading-relaxed">
            How much you <strong className="text-white">add on top of the cost</strong>{" "}
            to get your selling price. This is what most tradies quote on — "I
            add 25% to my materials".
          </p>
          <div className="mt-5 font-mono text-sm text-zinc-400 border-l-2 border-[var(--primary)] pl-4">
            Buy for $100. Add 25% markup = <span className="text-white">$125 sell</span>.
            <br />Profit = $25. Margin = only{" "}
            <span className="text-[var(--primary)]">20%</span>.
          </div>
        </div>
        <div className="p-4 sm:p-6 md:p-8 bg-[var(--surface)]">
          <div className="flex items-center gap-2">
            <TrendingUp size={16} className="text-[var(--secondary)]" />
            <div className="label-mono">MARGIN</div>
          </div>
          <div className="font-mono text-lg mt-3 text-[var(--secondary)]">
            profit ÷ <span className="text-white">sell price</span>
          </div>
          <p className="text-zinc-300 mt-4 text-sm leading-relaxed">
            How much of <strong className="text-white">every dollar you invoice</strong>{" "}
            is actually profit. This is what your accountant — and your bank
            account — cares about.
          </p>
          <div className="mt-5 font-mono text-sm text-zinc-400 border-l-2 border-[var(--secondary)] pl-4">
            Want a 25% margin on a $100 cost? Sell for{" "}
            <span className="text-white">$133.33</span>.
            <br />Markup = <span className="text-[var(--secondary)]">33.3%</span>.
          </div>
        </div>
      </div>

      {/* CALCULATOR */}
      <div className="mt-10 grid lg:grid-cols-5 gap-0 border border-[var(--border)]">
        <div className="lg:col-span-3 p-4 sm:p-6 md:p-8 border-r border-[var(--border)]">
          <div className="label-mono mb-3">{t("margin.know", "I know my…")}</div>
          <div className="grid grid-cols-3 gap-0 border border-[var(--border)] mb-8">
            {[
              { k: "markup", label: t("margin.mode.markup", "Cost + Markup %") },
              { k: "margin", label: t("margin.mode.margin", "Cost + Margin %") },
              { k: "price", label: t("margin.mode.price", "Cost + Sell $") },
            ].map((tab, i) => (
              <button
                key={tab.k}
                onClick={() => setMode(tab.k)}
                data-testid={`mm-mode-${tab.k}`}
                className={`px-3 py-3 font-mono text-xs uppercase tracking-wider ${
                  i > 0 ? "border-l border-[var(--border)]" : ""
                } ${
                  mode === tab.k
                    ? "bg-[var(--primary)] text-black"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="label-mono mb-2">{t("margin.cost", "Cost (NZD)")}</div>
              <input
                type="number"
                step="0.01"
                className="input-sharp text-2xl py-4"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                data-testid="mm-cost-input"
              />
            </div>
            {mode !== "price" && (
              <div>
                <div className="label-mono mb-2">
                  {mode === "margin" ? t("margin.target_margin", "Target margin %") : t("margin.markup_pct", "Markup %")}
                </div>
                <input
                  type="number"
                  step="0.1"
                  className="input-sharp text-2xl py-4"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  data-testid="mm-value-input"
                />
              </div>
            )}
            {mode === "price" && (
              <div>
                <div className="label-mono mb-2">{t("margin.sell_nzd", "Sell price (NZD)")}</div>
                <input
                  type="number"
                  step="0.01"
                  className="input-sharp text-2xl py-4"
                  value={sell}
                  onChange={(e) => setSell(e.target.value)}
                  data-testid="mm-sell-input"
                />
              </div>
            )}
          </div>

          {/* THE CLASSIC MISTAKE */}
          {mode === "margin" && meantMargin && (
            <div
              className="mt-8 border border-[var(--secondary)]/50 bg-[var(--secondary)]/10 p-5"
              data-testid="mm-mistake-panel"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle
                  size={18}
                  className="text-[var(--secondary)] shrink-0 mt-0.5"
                />
                <div>
                  <div className="font-mono text-sm uppercase tracking-wider text-[var(--secondary)]">
                    The classic mistake
                  </div>
                  <p className="text-zinc-200 text-sm mt-2 leading-relaxed">
                    If you wanted a {fmtNumber(meantMargin.pct, 1)}% margin but
                    added {fmtNumber(meantMargin.pct, 1)}% as markup instead,
                    you'd quote{" "}
                    <span className="font-mono text-white">
                      {fmtMoney(meantMargin.mistakenSell)}
                    </span>{" "}
                    when the right number is{" "}
                    <span className="font-mono text-[var(--primary)]">
                      {fmtMoney(meantMargin.correctSell)}
                    </span>
                    .
                  </p>
                  <p className="text-zinc-400 text-sm mt-2 font-mono">
                    That's{" "}
                    <span className="text-[var(--secondary)]">
                      {fmtMoney(meantMargin.lost)} left on the table
                    </span>{" "}
                    — every single job.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Ticket */}
        <div className="lg:col-span-2 p-4 sm:p-6 md:p-8 bg-[var(--surface)]">
          <div className="flex items-center gap-2">
            <Calculator size={16} className="text-[var(--primary)]" />
            <div className="label-mono">{t("margin.ticket", "Ticket")}</div>
          </div>
          <div className="mt-4 space-y-3 font-mono">
            <Row label={t("margin.row.cost", "Cost")} value={fmtMoney(r.cost)} testid="mm-out-cost" />
            <Row label={t("margin.row.sell", "Sell")} value={fmtMoney(r.sell)} testid="mm-out-sell" big />
            <Row
              label={t("margin.row.profit", "Profit")}
              value={fmtMoney(r.profit)}
              testid="mm-out-profit"
              accent
            />
            <div className="border-t border-[var(--border)] pt-3">
              <Row
                label={t("margin.row.markup", "Markup")}
                value={`${fmtNumber(r.markup, 2)}%`}
                testid="mm-out-markup"
                accent
              />
              <Row
                label={t("margin.row.margin", "Margin")}
                value={`${fmtNumber(r.margin, 2)}%`}
                testid="mm-out-margin"
                secondary
              />
            </div>
          </div>
          <div className="hi-vis-stripe h-1.5 mt-8 -mx-4 sm:-mx-6 md:-mx-8 -mb-4 sm:-mb-6 md:-mb-8" />
        </div>
      </div>

      {/* REFERENCE TABLE */}
      <div className="mt-10 grid lg:grid-cols-5 gap-0 border border-[var(--border)]">
        <div className="lg:col-span-2 p-4 sm:p-6 md:p-8 border-r border-[var(--border)]">
          <div className="flex items-center gap-2 mb-3">
            <Info size={16} className="text-[var(--primary)]" />
            <div className="label-mono">Rule of thumb</div>
          </div>
          <p className="text-zinc-300 text-sm leading-relaxed">
            A given markup always produces a{" "}
            <span className="text-[var(--primary)]">lower</span> margin. The
            bigger the percentage, the bigger the gap.
          </p>
          <div className="font-mono text-sm mt-5 border-l-2 border-[var(--primary)] pl-4 text-zinc-300 leading-relaxed">
            markup → margin<br />
            margin = markup ÷ (1 + markup)<br />
            markup = margin ÷ (1 − margin)
          </div>
          <div className="mt-6 text-sm text-zinc-400 leading-relaxed">
            On the right — a cheat sheet. Print it, stick it in the van.
          </div>
        </div>
        <div className="lg:col-span-3">
          <div className="grid grid-cols-2 gap-0 text-xs font-mono uppercase tracking-wider text-zinc-500 border-b border-[var(--border)]">
            <div className="p-3">Markup %</div>
            <div className="p-3 border-l border-[var(--border)]">
              Equivalent Margin %
            </div>
          </div>
          {REFERENCE.map((row) => (
            <div
              key={row.markup}
              className="grid grid-cols-2 gap-0 text-sm font-mono border-b border-[var(--border)] last:border-b-0"
              data-testid={`mm-ref-${row.markup}`}
            >
              <div className="p-3 text-[var(--primary)]">
                {fmtNumber(row.markup, 2)}%
              </div>
              <div className="p-3 border-l border-[var(--border)] text-[var(--secondary)]">
                {fmtNumber(row.margin, 2)}%
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10 text-xs font-mono text-zinc-500 max-w-3xl">
        Tip — when you're told "industry margin is 30%", that means 30% of the
        sell price is profit (so mark materials up by ~42.9%, not 30%). Always
        check which word was actually used.
      </div>

      <NudgeCard
        show={r.margin > 0 && r.margin < 15}
        tone="warning"
        title={`Tight margin (${fmtNumber(r.margin, 1)}%)`}
        body="Most NZ trades target 25–40% margins to cover overhead, downtime and a real wage. Sub-15% means one bad job and you're underwater. Your accountant can help you set proper rates."
        cta="Book a pricing review"
        campaign="margin-low-nudge"
      />

      <EmailResultsCard
        calculator="margin-markup"
        payload={{
          mode,
          cost: r.cost,
          sell: r.sell,
          profit: r.profit,
          margin_pct: r.margin,
          markup_pct: r.markup,
        }}
        labelKey="email.margin.label"
        descKey="email.margin.desc"
      />

      <HireUsCTA
        campaign="margin-calc"
        compact
        headline="Pricing strategy is half the game."
        subline="A good accountant doesn't just file your return — we help you set rates that keep you in business. Have a chat."
      />
    </div>
  );
}

function Row({ label, value, big, accent, secondary, testid }) {
  const color = accent
    ? "text-[var(--primary)]"
    : secondary
    ? "text-[var(--secondary)]"
    : "text-white";
  return (
    <div className="flex items-baseline justify-between">
      <span className="label-mono">{label}</span>
      <span
        data-testid={testid}
        className={`${big ? "text-3xl" : "text-base"} ${color}`}
      >
        {value}
      </span>
    </div>
  );
}
